import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import SparkleIcon from '@precis-digital/kurama/icons/SparkleIcon';
import InlineChip from '@precis-digital/kurama/InlineChip';
import Tooltip, { TooltipTypography } from '@precis-digital/kurama/Tooltip';
import { styled } from '@precis-digital/kurama/utils';
import { type ComponentProps, type ReactElement, type ReactNode, useMemo } from 'react';
import { useSkills } from 'shared/copilot/api/skills';
import { SkillTooltipDescription } from 'shared/copilot/components/SkillTooltipDescription';
import { useCurrentClient } from 'shared/context/ClientContext';
import type Markdown from 'shared/markdown';
import { useTranslation } from 'shared/translations';
import { type Skill } from 'src/copilot/types/skills';
import { type FlowSkillScope } from 'shared/api/copilot';
import { getFlowSkillChipColor, mapFlowSkillScopeToAlvie } from 'src/copilot/utils/mapFlowSkill';
import { buildSkillPillMarkdown, SKILL_PILL_TAG } from 'src/copilot/utils/skillTags';
interface CopilotSkillPillProps {
  id: string;
  title: string;
  scope: FlowSkillScope;
  skill?: Skill;
}

/**
 * Flow-style skill pill for sent messages in Copilot Pro chat transcripts (a Kurama `InlineChip`).
 * Not used in the composer: its slash chips are raw HTML inside a `contenteditable`, so they only
 * share the `InlineChip` styles and the scope colour, not this component or its tooltip content.
 */
export const CopilotSkillPill = t0 => {
  const $ = _c(47);
  const {
    id,
    title,
    scope,
    skill
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot.composer"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const resolvedTitle = skill?.name ?? title;
  const description = skill?.description ?? "";
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let updatedLabel;
  if ($[1] !== description || $[2] !== scope || $[3] !== skill || $[4] !== t) {
    const knowledgeNames = (skill?.knowledgeSources ?? []).map(_temp).filter(Boolean);
    const dataSourceNames = (skill?.dataSources ?? []).map(_temp2).filter(Boolean);
    let t6;
    if ($[11] !== scope) {
      t6 = mapFlowSkillScopeToAlvie(scope);
      $[11] = scope;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    const t7 = `skillScope.${t6}`;
    let t8;
    if ($[13] !== t || $[14] !== t7) {
      t8 = t(t7);
      $[13] = t;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    const scopeLabel = t8;
    let t9;
    if ($[16] !== skill || $[17] !== t) {
      t9 = skill != null ? t("skillUpdated", {
        date: new Date(skill.updatedAt).toLocaleDateString()
      }) : null;
      $[16] = skill;
      $[17] = t;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    updatedLabel = t9;
    T0 = StyledTooltipBody;
    if ($[19] !== scopeLabel) {
      t2 = <TooltipTypography variant="body3">{scopeLabel}</TooltipTypography>;
      $[19] = scopeLabel;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    if ($[21] !== description) {
      t3 = description !== "" ? <SkillTooltipDescription variant="body2">{description}</SkillTooltipDescription> : null;
      $[21] = description;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    t4 = dataSourceNames.length > 0 ? <TooltipTypography variant="body3">{t("skillDataSources", {
        sources: dataSourceNames.join(", ")
      })}</TooltipTypography> : null;
    t5 = knowledgeNames.length > 0 ? <TooltipTypography variant="body3">{t("skillsAddsKnowledge", {
        knowledge: knowledgeNames.join(", ")
      })}</TooltipTypography> : null;
    $[1] = description;
    $[2] = scope;
    $[3] = skill;
    $[4] = t;
    $[5] = T0;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = updatedLabel;
  } else {
    T0 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    updatedLabel = $[10];
  }
  let t6;
  if ($[23] !== updatedLabel) {
    t6 = updatedLabel != null ? <TooltipTypography variant="body3">{updatedLabel}</TooltipTypography> : null;
    $[23] = updatedLabel;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== T0 || $[26] !== t2 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6) {
    t7 = <T0>{t2}{t3}{t4}{t5}{t6}</T0>;
    $[25] = T0;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const tooltipBody = t7;
  let t8;
  if ($[32] !== scope) {
    t8 = getFlowSkillChipColor(scope);
    $[32] = scope;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <StyledSparkleIcon />;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== resolvedTitle || $[36] !== t) {
    t10 = t("skillPillAria", {
      title: resolvedTitle
    });
    $[35] = resolvedTitle;
    $[36] = t;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== id || $[39] !== resolvedTitle || $[40] !== t10 || $[41] !== t8) {
    t11 = <StyledSkillPillAnchor><InlineChip label={resolvedTitle} color={t8} leftIcon={t9} aria-label={t10} data-skill-id={id} /></StyledSkillPillAnchor>;
    $[38] = id;
    $[39] = resolvedTitle;
    $[40] = t10;
    $[41] = t8;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== resolvedTitle || $[44] !== t11 || $[45] !== tooltipBody) {
    t12 = <Tooltip kind="multiline" title={resolvedTitle} body={tooltipBody} width="320px" placement="top" shouldDelay={true}>{t11}</Tooltip>;
    $[43] = resolvedTitle;
    $[44] = t11;
    $[45] = tooltipBody;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  return t12;
};
type MarkdownComponentType = (props: ComponentProps<typeof Markdown>) => ReactNode;
interface CopilotSkillMessageContentProps {
  content: string;
  /** Transcript markdown renderer (e.g. the compact Copilot `Markdown`); receives the pill override. */
  MarkdownComponent: MarkdownComponentType;
}
interface SkillPillElementProps {
  'data-index'?: string;
}

/**
 * Renders a user message with Flow `<skill … />` tags as scope-coloured pills
 * (same visual language as the composer slash chips).
 * Tags become `skill-pill` placeholders so the whole message goes through one full markdown
 * pass — text next to a pill renders exactly like a message without skills.
 */
export const CopilotSkillMessageContent = t0 => {
  const $ = _c(12);
  const {
    content,
    MarkdownComponent
  } = t0;
  let t1;
  if ($[0] !== content) {
    t1 = buildSkillPillMarkdown(content);
    $[0] = content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    markdown,
    pills
  } = t1;
  const hasSkill = pills.length > 0;
  const {
    currentClient
  } = useCurrentClient();
  const {
    data: remoteSkills
  } = useSkills(currentClient.id, hasSkill);
  let map;
  if ($[2] !== remoteSkills) {
    map = new Map();
    for (const skill of remoteSkills ?? []) {
      map.set(String(skill.id), skill);
    }
    $[2] = remoteSkills;
    $[3] = map;
  } else {
    map = $[3];
  }
  const skillsById = map;
  let t2;
  if ($[4] !== pills || $[5] !== skillsById) {
    t2 = {
      [SKILL_PILL_TAG]: t3 => {
        const {
          "data-index": rawIndex
        } = t3;
        const pill = rawIndex == null ? undefined : pills[Number(rawIndex)];
        if (pill == null) {
          return null;
        }
        return <CopilotSkillPill id={pill.id} title={pill.title} scope={pill.scope} skill={skillsById.get(pill.id)} />;
      }
    };
    $[4] = pills;
    $[5] = skillsById;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const components = t2;
  let t3;
  if ($[7] !== MarkdownComponent || $[8] !== components || $[9] !== hasSkill || $[10] !== markdown) {
    t3 = <MarkdownComponent allowRawHtml={hasSkill} components={components}>{markdown}</MarkdownComponent>;
    $[7] = MarkdownComponent;
    $[8] = components;
    $[9] = hasSkill;
    $[10] = markdown;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
const StyledSparkleIcon = styled(SparkleIcon)(() => ({
  width: 12,
  height: 12,
  flexShrink: 0,
  opacity: 0.75
}));
const StyledTooltipBody = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  width: '100%'
}));
const StyledSkillPillAnchor = styled('span')(() => ({
  display: 'inline-flex',
  verticalAlign: 'middle',
  cursor: 'help'
}));
function _temp(source) {
  return source.name;
}
function _temp2(source_0) {
  return source_0.name;
}